import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Temporary enum to replace missing EnumDeviceSensorsUiType
const EnumDeviceSensorsUiType = {
    Temp: 'temp',
    Rpm: 'rpm',
    Ph: 'ph',
};
const Card3DView = ({ device, liveSensorValues, }) => {
    const getSensorStyle = (type, value) => {
        switch (type) {
            case 'temp':
            case EnumDeviceSensorsUiType.Temp:
                return {
                    bg: 'from-red-400 via-red-500 to-red-600',
                    border: 'border-red-400',
                    text: 'text-red-100',
                    icon: '🌡️',
                    statusColor: value !== null && value > 25 ? 'bg-red-300' : 'bg-green-300',
                    accent: 'bg-red-500',
                    shadow: 'shadow-red-500/25',
                };
            case 'rpm':
            case EnumDeviceSensorsUiType.Rpm:
                return {
                    bg: 'from-blue-400 via-blue-500 to-blue-600',
                    border: 'border-blue-400',
                    text: 'text-blue-100',
                    icon: '⚡',
                    statusColor: value !== null && value > 1000 ? 'bg-yellow-300' : 'bg-green-300',
                    accent: 'bg-blue-500',
                    shadow: 'shadow-blue-500/25',
                };
            case 'ph':
            case EnumDeviceSensorsUiType.Ph:
                return {
                    bg: 'from-green-400 via-green-500 to-green-600',
                    border: 'border-green-400',
                    text: 'text-green-100',
                    icon: '💧',
                    statusColor: value !== null && (value < 6.5 || value > 8.5)
                        ? 'bg-yellow-300'
                        : 'bg-green-300',
                    accent: 'bg-green-500',
                    shadow: 'shadow-green-500/25',
                };
            default:
                return {
                    bg: 'from-gray-400 via-gray-500 to-gray-600',
                    border: 'border-gray-400',
                    text: 'text-gray-100',
                    icon: '📊',
                    statusColor: 'bg-gray-300',
                    accent: 'bg-gray-500',
                    shadow: 'shadow-gray-500/25',
                };
        }
    };
    const formatSensorValue = (value, type) => {
        console.log('formatSensorValue called with:', {
            value,
            type,
            sensorName: 'debug',
        });
        // More robust null/undefined check
        if (value === null || value === undefined || typeof value === 'undefined') {
            console.log('Value is null or undefined, returning N/A');
            return 'N/A';
        }
        // Check if it's a valid number
        if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
            console.log('Value is not a valid number:', typeof value, value);
            return 'Invalid';
        }
        try {
            switch (type) {
                case 'temp':
                case EnumDeviceSensorsUiType.Temp:
                    return `${value.toFixed(1)}°C`;
                case 'rpm':
                case EnumDeviceSensorsUiType.Rpm:
                    return `${value.toFixed(0)} Hz`;
                case 'ph':
                case EnumDeviceSensorsUiType.Ph:
                    return `${value.toFixed(1)} pH`;
                default:
                    return value.toString();
            }
        }
        catch (error) {
            console.error('Error in formatSensorValue:', error);
            return 'Error';
        }
    };
    const sensors = device.sensorsUI || [];
    return (_jsxs("div", { className: "bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl shadow-2xl border border-gray-700 p-6 min-h-[600px]", children: [_jsxs("div", { className: "text-center mb-8", children: [_jsx("h2", { className: "text-3xl font-bold text-white mb-2", children: device.deviceLabel || device.deviceName || '3D Sensor Cards' }), _jsx("p", { className: "text-gray-300", children: "Interactive 3D card visualization" })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 perspective-1000", children: sensors.map((sensor, index) => {
                    var _a;
                    if (!sensor)
                        return null;
                    const value = liveSensorValues[sensor.sensorName];
                    const formattedValue = formatSensorValue(value, sensor.type);
                    const styles = getSensorStyle(sensor.type, value);
                    const isAlert = styles.statusColor !== 'bg-green-300';
                    // Create varying rotation angles for 3D effect
                    const rotateY = Math.sin(index * 0.5) * 15;
                    const rotateX = Math.cos(index * 0.3) * 10;
                    return (_jsxs("div", { className: "group", style: {
                            transformStyle: 'preserve-3d',
                            transform: `rotateY(${rotateY}deg) rotateX(${rotateX}deg)`,
                        }, children: [_jsxs("div", { className: `
                relative bg-gradient-to-br ${styles.bg}
                border-2 ${styles.border}
                rounded-2xl p-6 h-48
                transform transition-all duration-500
                group-hover:rotate-y-180 group-hover:scale-105
                shadow-2xl ${styles.shadow}
                cursor-pointer overflow-hidden
              `, children: [_jsxs("div", { className: "absolute inset-0 p-6 flex flex-col justify-between backface-hidden", children: [_jsxs("div", { className: "flex justify-between items-start", children: [_jsx("div", { className: "text-4xl", children: styles.icon }), _jsx("div", { className: `w-3 h-3 rounded-full ${styles.statusColor} animate-pulse` })] }), _jsxs("div", { children: [_jsx("div", { className: "text-2xl font-bold text-white mb-2", children: formattedValue }), _jsx("div", { className: "text-sm font-medium text-gray-200", children: sensor.label })] }), _jsxs("div", { className: "flex justify-between items-end", children: [_jsx("div", { className: "text-xs text-gray-300", children: (_a = sensor.type) === null || _a === void 0 ? void 0 : _a.toUpperCase() }), _jsx("div", { className: `w-8 h-1 ${styles.accent} rounded-full opacity-70` })] })] }), _jsx("div", { className: "absolute inset-0 p-6 flex flex-col justify-center items-center backface-hidden transform rotate-y-180 bg-black/50", children: _jsxs("div", { className: "text-white text-center", children: [_jsx("div", { className: "text-2xl mb-2", children: styles.icon }), _jsx("div", { className: "text-lg font-bold mb-2", children: sensor.label }), _jsxs("div", { className: "text-sm mb-1", children: ["Sensor: ", sensor.sensorName] }), _jsxs("div", { className: "text-sm mb-1", children: ["Type: ", sensor.type] }), _jsxs("div", { className: "text-sm", children: ["Status: ", isAlert ? '⚠️ Alert' : '✅ Normal'] })] }) }), _jsx("div", { className: "absolute inset-0 rounded-2xl border border-white/10 pointer-events-none" }), _jsx("div", { className: "absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent" })] }), _jsx("div", { className: "absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-4/5 h-4 bg-black/30 rounded-full blur-lg opacity-50 group-hover:opacity-70 transition-opacity duration-300" })] }, `sensor-${index}`));
                }) }), _jsx("div", { className: "absolute inset-0 pointer-events-none overflow-hidden rounded-2xl", children: [...Array(20)].map((_, i) => (_jsx("div", { className: "absolute w-1 h-1 bg-white/20 rounded-full animate-float", style: {
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 5}s`,
                        animationDuration: `${10 + Math.random() * 10}s`,
                    } }, i))) }), _jsx("style", { children: `
        .perspective-1000 {
          perspective: 1000px;
        }
        
        .backface-hidden {
          backface-visibility: hidden;
        }
        
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
        }
        
        .animate-float {
          animation: float linear infinite;
        }
      ` })] }));
};
export default Card3DView;
